import React, { useState, useEffect } from 'react';
import { contactInfoAPI } from '../../services/api';

const DEFAULT_WORKING_HOURS = [
  { days: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
  { days: 'Saturday',         hours: '10:00 AM – 4:00 PM' },
  { days: 'Sunday',           hours: 'Closed' },
];

const ContactInfo = () => {
  const [form, setForm]       = useState({
    address:       '',
    phone:         '',
    email:         '',
    working_hours: DEFAULT_WORKING_HOURS,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    const load = async () => {
      try {
        const res = await contactInfoAPI.get();
        if (res.success) setForm(res.data);
      } catch {
        setMessage({ type: 'error', text: 'Failed to load contact info.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleHourChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.working_hours];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, working_hours: updated };
    });
  };

  const addHourRow = () => {
    setForm((prev) => ({
      ...prev,
      working_hours: [...prev.working_hours, { days: '', hours: '' }],
    }));
  };

  const removeHourRow = (index) => {
    setForm((prev) => ({
      ...prev,
      working_hours: prev.working_hours.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const res = await contactInfoAPI.update(form);
      if (res.success) {
        setMessage({ type: 'success', text: 'Contact info saved successfully.' });
      }
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        setMessage({ type: 'error', text: Array.isArray(first) ? first[0] : first });
      } else {
        setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '2rem' }}>
      <h2 style={{ marginBottom: 4 }}>Contact Info</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: 14 }}>
        This information is displayed on the website's Contact page.
      </p>

      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 8,
          marginBottom: '1.5rem',
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color:      message.type === 'success' ? '#166534' : '#991b1b',
          border:     `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          fontSize: 14,
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Address */}
        <div>
          <label style={labelStyle}>Office Address</label>
          <textarea
            required
            rows={4}
            value={form.address}
            onChange={(e) => handleChange('address', e.target.value)}
            style={inputStyle}
            placeholder="Full office address"
          />
        </div>

        {/* Phone */}
        <div>
          <label style={labelStyle}>Phone & WhatsApp</label>
          <input
            type="text"
            required
            value={form.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            style={inputStyle}
            placeholder="+91 XXXXX XXXXX"
          />
        </div>

        {/* Email */}
        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            style={inputStyle}
            placeholder="email@example.com"
          />
        </div>

        {/* Working Hours */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Working Hours</label>
            <button type="button" onClick={addHourRow} style={addBtnStyle}>+ Add Row</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {form.working_hours.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  required
                  value={row.days}
                  onChange={(e) => handleHourChange(i, 'days', e.target.value)}
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  placeholder="e.g. Monday – Friday"
                />
                <input
                  type="text"
                  required
                  value={row.hours}
                  onChange={(e) => handleHourChange(i, 'hours', e.target.value)}
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }}
                  placeholder="e.g. 9:00 AM – 6:00 PM"
                />
                {form.working_hours.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeHourRow(i)}
                    style={removeBtnStyle}
                    title="Remove row"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            padding: '0.75rem 1.5rem',
            background: saving ? '#aaa' : '#630137',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 15,
            cursor: saving ? 'not-allowed' : 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const addBtnStyle = {
  fontSize: 13,
  padding: '0.3rem 0.75rem',
  background: '#f3f4f6',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  color: '#374151',
};

const removeBtnStyle = {
  padding: '0.3rem 0.6rem',
  background: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: 6,
  cursor: 'pointer',
  color: '#dc2626',
  fontSize: 13,
  flexShrink: 0,
};

export default ContactInfo;
